import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/common/Button';
import { isIOS, isSafari } from '@/utils/platform';

interface AuthScreenProps {
  onComplete: (displayName: string) => void;
  title?: string;
}

// Minimum time (ms) user must spend on form before submission is allowed
const MIN_FORM_TIME_MS = 2000;
// Minimum interactions required (mouse moves, key presses, touches)
const MIN_INTERACTIONS = 3;

/**
 * Authentication screen - offers Google sign-in or guest login with display name
 * Includes bot protection: honeypot, timing checks, interaction detection
 */
export const AuthScreen: React.FC<AuthScreenProps> = ({ 
  onComplete,
  title = 'SIGN IN'
}) => {
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastInitial, setLastInitial] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [popupBlockAttempts, setPopupBlockAttempts] = useState(0);
  
  // Bot protection state
  const [honeypot, setHoneypot] = useState(''); // Hidden field - should remain empty
  const formLoadTime = useRef<number>(Date.now());
  const interactionCount = useRef<number>(0);
  const hasInteracted = useRef<boolean>(false);
  
  const { signInAnonymously, signInWithGoogle, isAuthenticated, user } = useAuth();

  // Track user interactions (mouse, touch, keyboard)
  useEffect(() => {
    const trackInteraction = () => {
      interactionCount.current += 1;
      hasInteracted.current = true;
    };

    window.addEventListener('mousemove', trackInteraction);
    window.addEventListener('touchstart', trackInteraction);
    window.addEventListener('keydown', trackInteraction);

    return () => {
      window.removeEventListener('mousemove', trackInteraction);
      window.removeEventListener('touchstart', trackInteraction);
      window.removeEventListener('keydown', trackInteraction);
    };
  }, []);

  // Reset form load time when showing guest form
  useEffect(() => {
    if (showGuestForm) {
      formLoadTime.current = Date.now();
    }
  }, [showGuestForm]);

  const displayName = firstName && lastInitial 
    ? `${firstName} ${lastInitial.toUpperCase()}`
    : firstName || '';

  const isValid = firstName.trim().length >= 2 && lastInitial.trim().length === 1;

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // On retry after popup block, try to re-trigger browser's popup permission UI
      if (popupBlocked) {
        const testWindow = window.open('', '_blank');
        if (testWindow) {
          testWindow.close(); // Popup was allowed — proceed with sign-in
        }
        // If null, browser showed the notification again
      }
      await signInWithGoogle();
      // Google provides displayName from their account
      // Wait for auth state to update, then complete
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in with Google';
      const isPopupBlock = message.toLowerCase().includes('pop-up') || 
                           message.toLowerCase().includes('popup') || 
                           message.toLowerCase().includes('blocked');
      if (isPopupBlock) {
        setPopupBlocked(true);
        setPopupBlockAttempts(prev => prev + 1);
        setError(null); // Don't show raw error — the help panel handles messaging
      } else if (message === 'Redirecting to Google...') {
        // Redirect fallback triggered — don't show as error
        setError(null);
      } else {
        setPopupBlocked(false);
        setError(message);
      }
      setIsLoading(false);
    }
  };

  // Handle completion after Google sign-in
  React.useEffect(() => {
    if (isAuthenticated && user?.displayName && isLoading) {
      setIsLoading(false);
      onComplete(user.displayName);
    }
  }, [isAuthenticated, user?.displayName, isLoading, onComplete]);

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) return;

    // Bot protection checks
    const timeSpent = Date.now() - formLoadTime.current;
    
    // Check 1: Honeypot field should be empty
    if (honeypot) {
      console.warn('Bot detected: honeypot filled');
      setError('Something went wrong. Please try again.');
      return;
    }
    
    // Check 2: Minimum time on form (bots are fast)
    if (timeSpent < MIN_FORM_TIME_MS) {
      console.warn('Bot detected: form submitted too quickly');
      setError('Please take a moment to fill out the form.');
      return;
    }
    
    // Check 3: Require some user interaction
    if (!hasInteracted.current || interactionCount.current < MIN_INTERACTIONS) {
      console.warn('Bot detected: insufficient interaction');
      setError('Something went wrong. Please try again.');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      await signInAnonymously(displayName);
      onComplete(displayName);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to continue');
    } finally {
      setIsLoading(false);
    }
  };

  // Show guest name entry form
  if (showGuestForm) {
    return (
      <div className="page-fixed flex flex-col items-center justify-center p-6">
        <form onSubmit={handleGuestSubmit} className="w-full max-w-sm space-y-6">
          <button
            type="button"
            onClick={() => setShowGuestForm(false)}
            className="text-text-secondary hover:text-text-primary transition-colors"
          >
            ← Back
          </button>

          <h1 className="text-3xl font-bold text-center text-text-primary">WHO ARE YOU?</h1>
          
          <p className="text-center text-sm text-text-secondary">
            ⚠️ Guest accounts are temporary. If you close your browser, you may lose access to your hunts.
          </p>

          {/* Honeypot field - hidden from users, bots will fill it */}
          <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
            <label htmlFor="website">Website</label>
            <input
              type="text"
              id="website"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm text-text-secondary mb-2">
                First Name
              </label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                maxLength={15}
                autoComplete="given-name"
                autoFocus
                className="w-full h-16 px-4 bg-surface text-text-primary text-xl rounded-xl border-2 border-surface-light focus:border-primary focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label htmlFor="lastInitial" className="block text-sm text-text-secondary mb-2">
                Last Initial
              </label>
              <input
                id="lastInitial"
                type="text"
                value={lastInitial}
                onChange={(e) => setLastInitial(e.target.value.slice(0, 1))}
                placeholder="D"
                maxLength={1}
                autoCapitalize="characters"
                className="w-24 h-16 px-4 bg-surface text-text-primary text-xl text-center rounded-xl border-2 border-surface-light focus:border-primary focus:outline-none transition-colors uppercase"
              />
            </div>
          </div>

          {displayName && (
            <p className="text-center text-text-secondary">
              You will appear as: <span className="text-text-primary font-medium">{displayName}</span>
            </p>
          )}

          {error && (
            <p className="text-center text-red-500 text-sm">{error}</p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={!isValid || isLoading}
            isLoading={isLoading}
          >
            CONTINUE AS GUEST
          </Button>
        </form>
      </div>
    );
  }

  // Main sign-in options
  return (
    <div className="page-fixed flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-4">
          <img 
            src="/GPSHuntLogo.png" 
            alt="GPS Hunt" 
            className="w-32 h-32 mx-auto object-contain"
          />
          <h1 className="text-4xl font-bold text-text-primary">{title}</h1>
          <p className="text-text-secondary">Choose how you want to sign in</p>
        </div>

        {popupBlocked && (
          <div className="bg-amber-900/30 border border-amber-500/40 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2 text-amber-400 font-semibold">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Pop-up Blocked
            </div>
            <p className="text-text-secondary text-sm">
              Your browser blocked the sign-in window. To fix this:
            </p>
            <ol className="text-text-secondary text-sm space-y-1.5 list-decimal pl-5">
              <li>
                Look for a notification {isIOS() || isSafari() 
                  ? 'at the top of Safari' 
                  : 'in your browser\'s address bar'} 
                <span className="inline-block animate-bounce ml-1">↑</span>
              </li>
              <li>
                Tap {isIOS() || isSafari() 
                  ? '"Allow" on the pop-up notification' 
                  : 'the blocked popup icon and allow pop-ups'}
              </li>
              <li>Then tap the button below to try again</li>
            </ol>
            {popupBlockAttempts >= 2 && (
              <div className="bg-surface/50 rounded-lg p-3 mt-2">
                <p className="text-text-secondary text-xs">
                  <strong className="text-text-primary">Still blocked?</strong> You can join the hunt instantly using Guest mode — no popup needed.
                </p>
              </div>
            )}
          </div>
        )}

        {error && !popupBlocked && (
          <p className="text-center text-red-500 text-sm">{error}</p>
        )}

        <div className="space-y-4">
          {/* Google Sign-In - Recommended */}
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            isLoading={isLoading}
            className="flex items-center justify-center gap-3"
          >
            {popupBlocked ? (
              <>
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
                TRY SIGN IN AGAIN
              </>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                SIGN IN WITH GOOGLE
              </>
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-surface-light"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-background text-text-secondary">or</span>
            </div>
          </div>

          {/* Guest Sign-In */}
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={() => setShowGuestForm(true)}
            disabled={isLoading}
          >
            CONTINUE AS GUEST
          </Button>
        </div>

        <p className="text-center text-xs text-text-secondary">
          Google sign-in keeps your account across sessions.
          <br />
          Guest accounts are temporary and may be lost.
        </p>

        <p className="text-center text-xs text-text-secondary mt-8">
          By continuing, you agree to the{' '}
          <a href="/terms" className="text-primary hover:underline">Terms of Use</a>
          {' '}and{' '}
          <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>
        </p>
      </div>
    </div>
  );
};

export default AuthScreen;
